/**
 * Pinia decoration-rollup store -- ADR 0056 (per-store Decorations tab).
 *
 * Backs the read-only, decoration-first readiness rollup rendered by
 * StoreDecorations.vue. Wraps the backend endpoint merged in PR #1986:
 *
 *   GET /api/stores/<store_id>/decoration_rollup/
 *   -> { store_id, decorations: [ {decoration_id, artwork_id,
 *        lifecycle_state, width_in, height_in, method, placements: [...] },
 *        ... ] }
 *
 * The backend already dedupes by decoration and orders the list -- this
 * store does not re-sort or re-dedupe.
 *
 * Tenant + epoch hygiene mirrors stores/storeReadiness.js: an integer
 * ``epoch`` is bumped by ``clear()`` so a late-arriving fetch from a
 * prior tenant/store cannot repopulate state after impersonation stops
 * or the user navigates away.
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import api from "@/services/api";

export const useDecorationRollupStore = defineStore("decorationRollup", () => {
  // Keyed by storeId so navigating between stores doesn't require a
  // fresh Pinia instance.
  const rollupByStoreId = ref({});
  const loadingByStoreId = ref({});
  const errorByStoreId = ref({});
  const epoch = ref(0);

  function rollupFor(storeId) {
    return rollupByStoreId.value[storeId] || null;
  }
  function isLoading(storeId) {
    return !!loadingByStoreId.value[storeId];
  }
  function errorFor(storeId) {
    return errorByStoreId.value[storeId] || null;
  }

  async function fetchRollup(storeId) {
    const startEpoch = epoch.value;
    loadingByStoreId.value = { ...loadingByStoreId.value, [storeId]: true };
    errorByStoreId.value = { ...errorByStoreId.value, [storeId]: null };
    try {
      const { data } = await api.get(`/stores/${storeId}/decoration_rollup/`);
      if (startEpoch !== epoch.value) return null;
      rollupByStoreId.value = { ...rollupByStoreId.value, [storeId]: data };
      return data;
    } catch (err) {
      if (startEpoch !== epoch.value) return null;
      errorByStoreId.value = { ...errorByStoreId.value, [storeId]: err };
      throw err;
    } finally {
      loadingByStoreId.value = { ...loadingByStoreId.value, [storeId]: false };
    }
  }

  function clear() {
    rollupByStoreId.value = {};
    loadingByStoreId.value = {};
    errorByStoreId.value = {};
    epoch.value += 1;
  }

  return {
    // state
    rollupByStoreId,
    loadingByStoreId,
    errorByStoreId,
    // getters
    rollupFor,
    isLoading,
    errorFor,
    // actions
    fetchRollup,
    clear,
  };
});
